import { getCurrentAccountId, useCredentialsStore, useUserInfoStore } from "@helium10/re-core";
import { getUrlWithSearchParams } from "@helium10/re-core";
import type { IAdditionalMenuItem, IAdditionalMenuOptions } from "@helium10/re-features";
import { useProgressLoadStatus } from "@helium10/re-ui-components";
import queryString from "query-string";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  billingRoute,
  claimCasesRoute,
  inboundRoute,
  mrsLink,
  packFeeRoute,
  refundGenieRoute,
} from "@/core/links";
import { useRedirect } from "../../core/hooks/useRedirect";
import { useTranslation } from "../../i18n/useTranslation";
import { ActiveTab } from "../PackFee/store";
import { faUsdCircle } from "@fortawesome/pro-regular-svg-icons";
import { connectionsSelector } from "@/store/refundServiceStore";
import { useRefundServiceStore } from "@/store/refundServiceStore";
import { useRefundGenieStore } from "@/store/refundGenieStore";
import { SallerStatus } from "@/core/helpers/enum.helper";

export const useMenu = () => {
  const { t } = useTranslation();
  const { redirect } = useRedirect();
  const location = useLocation();
  const useStore = useRefundServiceStore;
  const { reportEnable } = useRefundGenieStore((s) => s.permission); // 这个权限来控制侧边栏的样式

  const data = useStore(connectionsSelector);
  const {
    acceptInfo: { ifAccept },
    setupInfo: { ifSetup },
    serviceInfo,
    acceptInfoIsResponse,
    setupInfoIsResponse,
  } = useStore();

  // 增加进入RG菜单的权限
  const userInfo = useUserInfoStore((s) => s.userInfo?.results);
  const checkLogin = useCredentialsStore((s) => s.checkLogin);

  const originItems = useMemo(
    (): IAdditionalMenuItem[] => [
      {
        id: "claim_overview",
        name: t("menu.claim_overview.name"),
        icon: "svg-dashboard",
        link: getUrlWithSearchParams(mrsLink, {}, { hasAccountId: true }),
        highlight: [
          {
            link: mrsLink,
            strict: true,
          },
        ],
      },
      {
        id: "claim_cases",
        name: t("menu.claim_cases.name"),
        icon: "svg-suggestions",
        link: getUrlWithSearchParams(claimCasesRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: claimCasesRoute,
            strict: true,
          },
        ],
      },
      {
        id: "inbound",
        name: t("menu.inbound.name"),
        icon: "icon_restock_suggestions",
        link: getUrlWithSearchParams(inboundRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: inboundRoute,
            strict: true,
          },
        ],
      },
      {
        id: "packfee",
        name: t("menu.packfee.name"),
        icon: "icon_orders",
        link: getUrlWithSearchParams(packFeeRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: packFeeRoute,
            strict: true,
          },
        ],
      },
      {
        id: "billing",
        name: t("menu.billing.name"),
        icon: "icon_financial_events",
        link: getUrlWithSearchParams(billingRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: billingRoute,
            strict: true,
          },
        ],
      },
      {
        id: "refundGenie",
        name: t("menu.refundGenie.name"),
        icon: "",
        faIcon: faUsdCircle,
        link: getUrlWithSearchParams(refundGenieRoute, {}, { hasAccountId: true }),
        highlight: [
          {
            link: refundGenieRoute,
            strict: true,
          },
          {
            link: `${refundGenieRoute}/:reportId`,
            strict: true,
          },
        ],
      },
    ],
    [t],
  );

  const { setStatus } = useProgressLoadStatus();

  const permissionRoute = useMemo(() => {
    if (!ifAccept && !ifSetup) {
      return "landing";
    } else if (!ifAccept && ifSetup) {
      return "feeTerms";
    } else if (
      !data.length ||
      !data.some(({ statusValue }) => statusValue === SallerStatus.Active)
    ) {
      return "onboarding";
    }
    return "";
  }, [data, ifAccept, ifSetup]);

  const items = useMemo((): IAdditionalMenuItem[] => {
    const fewerMenusRoute = ["onboarding", "landing", "feeTerms"];
    const targetPath =
      fewerMenusRoute.find((path) => location.pathname.endsWith(path)) ?? permissionRoute; // 当前路由 ?? 权限路由
    const refundGenieMenu = originItems.find((item) => item.id === "refundGenie")!;
    if (targetPath) {
      const targetMenu = {
        id: targetPath,
        name: t(`menu.${targetPath}.name`),
        icon: "svg-dashboard",
        link: getUrlWithSearchParams(`${mrsLink}/${targetPath}`, {}, { hasAccountId: true }),
        highlight: [
          {
            link: `${mrsLink}/${targetPath}`,
            strict: true,
          },
        ],
      };
      // 允许访问 RG 才显示两个菜单,不允许访问的时候只有一个菜单,没必要显示
      if (
        reportEnable ||
        (checkLogin && !checkLogin.is7DayTrial && userInfo && !userInfo.isLowerThanDiamond) // (接口没有返回数据前不能放开RG菜单)
      ) {
        return [targetMenu, refundGenieMenu];
      }
      return [];
    }
    if (serviceInfo && acceptInfoIsResponse && setupInfoIsResponse) {
      // 权限相关接口请求回来之后才能放出 RG 菜单
      if (
        reportEnable ||
        (checkLogin && !checkLogin.is7DayTrial && userInfo && !userInfo.isLowerThanDiamond)
      )
        return originItems;
      return originItems.filter((item) => item.id !== "refundGenie");
    }
    return [];
  }, [
    originItems,
    serviceInfo,
    acceptInfoIsResponse,
    setupInfoIsResponse,
    permissionRoute,
    reportEnable,
    userInfo,
    checkLogin,
    location,
  ]);

  const handleRouteChange = useCallback(
    (link: string) => {
      if (link.split("?")[0] !== location.pathname) {
        setStatus("start");
      }
      const foundMenuItem = items.find((item) => item.link === link);
      if (foundMenuItem) {
        // packFee 切换tabs使用
        if (link.includes("packFee")) {
          const accountId = queryString.parseUrl(link)?.query?.accountId;
          if (accountId) {
            sessionStorage.setItem(
              "PACKFEETAB",
              JSON.stringify({
                ...JSON.parse(sessionStorage.getItem("PACKFEETAB") ?? "{}"),
                [accountId as string]: ActiveTab.Product,
              }),
            );
          }
        }
        if (
          process.env.NODE_ENV === "development" ||
          link.includes("managed-refund-service") ||
          link.includes("claim_cases") ||
          link.includes("inbound") ||
          link.includes("billing")
        ) {
          redirect(link, { accountId: getCurrentAccountId() as string });
        } else return (window.location.href = link);
      }
    },
    [items, redirect, queryString],
  );

  const menu: IAdditionalMenuOptions | undefined = useMemo(() => {
    const isEmptyMenu =
      location.pathname.includes("onboarding") && !location.pathname.endsWith("onboarding");

    return isEmptyMenu
      ? {
          title: "",
          items: [],
        }
      : {
          items: items,
          title: "Managed Refund Service",
          iconsUrl: "/menu-sprite.svg",
          onRouteChange: handleRouteChange,
        };
  }, [handleRouteChange, items, location]);

  return {
    menu,
  };
};
